// TextureManager.js
// This class handles texture creation and blending for the island terrain

import * as THREE from 'three';
import BiomeManager from './BiomeManager.js';
import TerrainBlending from '../shaders/TerrainBlending.js';

/**
 * TextureManager - handles texture creation and blending for terrain
 */
class TextureManager {
  /**
   * Generate a control texture for shader-based texture blending
   * @param {Object[][]} biomeMap - 2D array of biome objects
   * @param {number[][]} heightMap - 2D array of height values
   * @param {number[][]} islandMask - 2D array indicating land/water
   * @returns {THREE.DataTexture} Control texture for use with shaders
   */
  static generateControlTexture(biomeMap, heightMap, islandMask) {
    const width = biomeMap[0].length;
    const height = biomeMap.length;
    
    // Debug the incoming maps
    console.log(`Generating control texture: ${width}x${height}`);
    console.log(`Sample height value: ${heightMap[Math.floor(height/2)][Math.floor(width/2)]}`);
    console.log(`Sample mask value: ${islandMask[Math.floor(height/2)][Math.floor(width/2)]}`);
    
    // Create data buffer for control texture (RGBA)
    const size = width * height;
    const data = new Uint8Array(4 * size);
    
    // Fill control texture data - correct orientation for the mesh rotation
    // Note: Since the mesh is rotated by -Math.PI/2 in x, we need to adjust our mapping
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Fix the coordinate mapping to match the rotated mesh
        // We're using flipped coordinates to solve the rotation issue
        const mappedX = x;
        const mappedY = y; // Flip Y to correct the rotation
        
        // Get the biome and values at the correct coordinates
        const biome = biomeMap[mappedY][mappedX];
        const heightValue = heightMap[mappedY][mappedX];
        const maskValue = islandMask[mappedY][mappedX];
        
        // Calculate the index in the 1D data array (normal ordering for texture data)
        const index = (y * width + x) * 4;
        
        // Set default values (all 0)
        data[index] = 0;     // Red
        data[index + 1] = 0; // Green
        data[index + 2] = 0; // Blue
        data[index + 3] = 0; // Alpha
        
        // Handle water specially to ensure correct rendering
        if (maskValue < 0.1) { // Deep water
          data[index] = 255; // Full red channel for water
          continue;
        } else if (maskValue < 0.2) { // Shallow water
          data[index] = 200; // Make sure this is still > 128 (0.5 in normalized value)
          continue;
        } else if (maskValue < 0.3) { // beach
          data[index] = 180; // Make sure this is still > 128 (0.5 in normalized value)
          continue;
        }
        
        // Handle different biome types
        switch (biome?.id) {
          case 'beach':
            // Beach in red channel
            data[index] = 180;
            break;
          case 'grassland':
            // Grassland in green channel
            data[index + 1] = 255;
            break;
          case 'forest':
            // Forest has both green and blue
            data[index + 1] = 180;
            data[index + 2] = 255;
            break;
          case 'rocky_terrain':
          case 'mountains':
            // Mountains/rocks in alpha channel
            data[index + 3] = 255;
            break;
          case 'snow':
            // Snow is handled by height in the shader
            // But give it some mountain influence
            data[index + 3] = 150;
            break;
          default:
            // If biome isn't recognized, use height to determine texture
            if (heightValue < 0.3) {
              data[index] = 200; // Beach (red channel)
            } else if (heightValue < 0.6) {
              data[index + 1] = 200; // Grass (green channel)
            } else if (heightValue < 0.8) {
              data[index + 3] = 200; // Rock (alpha channel)
            } else {
              data[index + 3] = 255; // Snow (high alpha + height)
            }
            break;
        }
        
        // Height also influences texture weights for mountains and snow
        if (heightValue > 0.85) {
          // Increase mountain/snow influence for high terrain
          data[index + 3] = Math.max(data[index + 3], Math.floor(200 + (heightValue - 0.85) * 500));
        }
      }
    }
    
    // Create a texture from the data
    const texture = new THREE.DataTexture(
      data,
      width,
      height,
      THREE.RGBAFormat,
      THREE.UnsignedByteType
    );
    
    // Make sure to use proper texture settings
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.flipY = true; // Flip the texture vertically to match Three.js UV orientation
    texture.needsUpdate = true;
    
    // Log success
    console.log("Control texture generated successfully");
    
    return texture;
  }
  
  /**
   * Create a terrain material with height-based blending of textures
   * @param {Array<Array<number>>} heightMap - 2D array of height values
   * @param {Array<Array<number>>} islandMask - 2D array of island mask values (0-1)
   * @param {Array<Array<Object>>} biomeMap - 2D array of biome data
   * @returns {THREE.Material} Material for the terrain
   */
  static createTerrainMaterial(heightMap, islandMask, biomeMap) {
    // Create height-based control texture
    const controlTexture = TextureManager.generateControlTexture(biomeMap, heightMap, islandMask);
    
    // Create some placeholder textures for now (would be real textures in the full implementation)
    const placeholderSandTexture = this.createPatternedTexture(240, 230, 140, 'sand');
    const placeholderGrassTexture = this.createPatternedTexture(30, 50, 19, 'grass');
    
    // Use a try/catch to identify problems early
    try {
      // Verify the textures are valid before creating material
      if (!placeholderSandTexture || !placeholderGrassTexture || !controlTexture) {
        console.error("Missing texture when creating terrain material");
      }
      
      // Create material with terrain blending shader
      const material = new THREE.ShaderMaterial({
        vertexShader: TerrainBlending.vertexShader,
        fragmentShader: TerrainBlending.fragmentShader,
        uniforms: {
          sandTexture: { value: placeholderSandTexture },
          grassTexture: { value: placeholderGrassTexture },
          controlTexture: { value: controlTexture },
          uTextureScale: { value: TerrainBlending.defaultUniforms.uTextureScale },
          uBeachThreshold: { value: TerrainBlending.defaultUniforms.uBeachThreshold },
          uTransitionSmoothness: { value: TerrainBlending.defaultUniforms.uTransitionSmoothness },
          uVisualizationMode: { value: TerrainBlending.defaultUniforms.uVisualizationMode }
        },
        shadowSide: THREE.FrontSide
      });
      material.receiveShadow = true;
      
      // Skip precompilation which is causing errors - we'll let Three.js handle this at render time
      
      return material;
    } catch (error) {
      console.error("Error creating terrain material:", error);
      
      // Fallback to a basic material
      return new THREE.MeshStandardMaterial({ color: 0x6b8e23 });
    }
  }
  
  /**
   * Create a patterned texture for testing that looks more like the actual material
   * @param {number} r - Red component (0-255)
   * @param {number} g - Green component (0-255)
   * @param {number} b - Blue component (0-255)
   * @param {string} type - Type of texture ('sand', 'grass', 'rock', 'snow')
   * @returns {THREE.Texture} A placeholder texture with a pattern
   */
  static createPatternedTexture(r, g, b, type) {
    // Create a larger canvas for more detailed textures
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    // Fill with base color
    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.fillRect(0, 0, size, size);
    
    // Add patterns based on texture type
    switch (type) {
      case 'sand':
        // Add small dots for sand
        for (let i = 0; i < 2000; i++) {
          const x = Math.random() * size;
          const y = Math.random() * size;
          const radius = Math.random() * 0.5 + 0.3;
          
          ctx.fillStyle = `rgba(${r - 50}, ${g - 50}, ${b - 70}, 0.5)`;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
        
      case 'grass':
        // Add small lines for grass
        for (let i = 0; i < 500; i++) {
          const x = Math.random() * size;
          const y = Math.random() * size;
          const length = Math.random() * 3 + 2;
          const angle = Math.random() * Math.PI;
          
          ctx.strokeStyle = `rgba(${r - 40}, ${g + 20}, ${b - 20}, 0.5)`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + Math.cos(angle) * length, y + Math.sin(angle) * length);
          ctx.stroke();
        }
        break;
        
      case 'rock':
        // Add noise and cracks for rock
        for (let y = 0; y < size; y += 4) {
          for (let x = 0; x < size; x += 4) {
            const noise = Math.random() * 25 - 10;
            const color = Math.min(255, Math.max(0, r + noise));
            
            ctx.fillStyle = `rgba(${color}, ${color}, ${color}, 0.3)`;
            ctx.fillRect(x, y, 4, 4);
          }
        }
        
        // Add some cracks
        for (let i = 0; i < 20; i++) {
          const x = Math.random() * size;
          const y = Math.random() * size;
          const length = Math.random() * 20 + 10;
          const angle = Math.random() * Math.PI;
          
          ctx.strokeStyle = 'rgba(40, 40, 40, 0.3)';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(x, y);
          
          // Create jagged line
          let currentX = x;
          let currentY = y;
          for (let j = 0; j < 5; j++) {
            const newAngle = angle + (Math.random() * 0.5 - 0.25);
            const segmentLength = Math.random() * 5 + 3;
            currentX += Math.cos(newAngle) * segmentLength;
            currentY += Math.sin(newAngle) * segmentLength;
            ctx.lineTo(currentX, currentY);
          }
          
          ctx.stroke();
        }
        break;
        
      case 'snow':
        // Add subtle texture for snow
        for (let y = 0; y < size; y += 6) {
          for (let x = 0; x < size; x += 6) {
            const noise = Math.random() * 15;
            
            ctx.fillStyle = `rgba(255, 255, 255, ${noise/100})`;
            ctx.fillRect(x, y, 6, 6);
          }
        }
        
        // Add a few snow sparkles
        for (let i = 0; i < 100; i++) {
          const x = Math.random() * size;
          const y = Math.random() * size;
          const radius = Math.random() * 1 + 0.5;
          
          ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
    }
    
    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    
    return texture;
  }
  
  /**
   * Helper method to create actual textures from files
   * In a real implementation, this would use TextureLoader and actual files
   * @param {string} path - Path to the texture file
   * @returns {Promise<THREE.Texture>} The loaded texture
   */
  static loadTexture(path) {
    return new Promise((resolve, reject) => {
      const loader = new THREE.TextureLoader();
      loader.load(
        path,
        (texture) => {
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          resolve(texture);
        },
        undefined,
        reject
      );
    });
  }
}

export default TextureManager; 